import React from 'react';
import { 
  FiMapPin, 
  FiUser, 
  FiCalendar, 
  FiCamera, 
  FiPaperclip, 
  FiMessageCircle,
  FiAlertTriangle
} from 'react-icons/fi';
import { Snag } from '../../../shared/types/snag';

interface SnagCardProps {
  snag: Snag;
  onDragStart: () => void;
}

const SnagCard: React.FC<SnagCardProps> = ({ snag, onDragStart }) => {
  const getPriorityClass = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'priority-critical';
      case 'High': return 'priority-high';
      case 'Medium': return 'priority-medium';
      case 'Low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div
      className={`snag-card ${getPriorityClass(snag.priority)}`}
      draggable
      onDragStart={onDragStart}
    >
      <div className="card-header">
        <div className={`priority-indicator ${getPriorityClass(snag.priority)}`}>
          {snag.priority}
        </div>
        {snag.tags.length > 0 && (
          <div className="card-tags">
            {snag.tags.slice(0, 2).map(tag => (
              <span key={tag} className="tag">{tag}</span>
            ))}
          </div>
        )}
      </div>

      <h4 className="card-title">{snag.title}</h4>
      <p className="card-description">{snag.description}</p>

      <div className="card-location">
        <FiMapPin size={14} />
        <span>{snag.location.area}</span>
      </div>

      <div className="card-assignee">
        <div className="assignee-avatar">
          {snag.assignedTo.avatar ? (
            <img src={snag.assignedTo.avatar} alt={snag.assignedTo.name} />
          ) : (
            <FiUser />
          )}
        </div>
        <div className="assignee-info">
          <span className="assignee-name">{snag.assignedTo.name}</span>
          <span className="assignee-trade">{snag.assignedTo.trade}</span>
        </div>
      </div>

      <div className="card-footer">
        <div className="card-meta">
          {snag.photos.length > 0 && (
            <div className="meta-item">
              <FiCamera size={14} />
              <span>{snag.photos.length}</span>
            </div>
          )}
          
          {snag.attachments.length > 0 && (
            <div className="meta-item">
              <FiPaperclip size={14} />
              <span>{snag.attachments.length}</span>
            </div>
          )}
          
          {snag.comments.length > 0 && (
            <div className="meta-item">
              <FiMessageCircle size={14} />
              <span>{snag.comments.length}</span>
            </div>
          )}
        </div>

        {snag.dueDate && (
          <div className={`due-date ${isOverdue(snag.dueDate) ? 'overdue' : ''}`}>
            {isOverdue(snag.dueDate) && <FiAlertTriangle size={12} />}
            <FiCalendar size={14} />
            <span>{formatDate(snag.dueDate)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SnagCard;
